import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, apiUpload, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, MessageSquare, Upload, FileSpreadsheet, Users as UsersIcon, Radio, ShieldAlert, Trophy, Save, Coins, Activity, CalendarPlus, Send, X as XIcon, RefreshCw, Download } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DualClock from "@/components/DualClock";
import Countdown from "@/components/Countdown";
import FixtureChat from "@/components/FixtureChat";
import WheelspinReveal from "@/components/WheelspinReveal";
import { ReschedulePropose, RescheduleDecision } from "@/components/RescheduleWizard";
import MatchTimeline from "@/components/MatchTimeline";
import MatchThreadButton from "@/components/MatchThreadButton";

function FieldingControls({ fixture, side, onDone }) {
  const [size, setSize] = useState(fixture.fielding?.size || 40);
  const [saving, setSaving] = useState(false);
  const mySideAgreed = fixture.fielding?.[`${side}_agreed`];
  const submit = async () => {
    if (!size || size < 10 || size > 100) { toast.error("Fielding size must be 10-100."); return; }
    setSaving(true);
    try {
      await api.post(`/fixtures/${fixture.id}/fielding`, { size: Number(size), side });
      toast.success(`Locked ${size}v${size} for ${side.toUpperCase()} side.`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-1" data-testid={`fielding-controls-${fixture.id}`}>
      <input
        type="number"
        min={10}
        max={100}
        value={size}
        onChange={(e) => setSize(e.target.value)}
        data-testid={`fielding-size-${fixture.id}`}
        className="w-14 rounded-none bg-[#05050A] border border-[#F4D03F]/40 p-1 text-center font-mono text-[10px]"
      />
      <button
        onClick={submit}
        disabled={saving}
        data-testid={`fielding-agree-${fixture.id}`}
        className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#F4D03F] hover:bg-yellow-300 disabled:opacity-40 px-2 py-1 font-bold"
      >
        {mySideAgreed && size === fixture.fielding?.size ? "Re-lock" : "Agree size"}
      </button>
    </div>
  );
}

/**
 * Iter66 · Compact opponent name/tag with hover card (logo + division + record)
 * and an optional ↗ thread chip to deep-link into Discord's match thread.
 * Pure presentational — all data comes from props.
 */
function OpponentChip({ team, division, threadId, guildId, variant = "name" }) {
  const [open, setOpen] = useState(false);
  const w = team?.wins ?? 0;
  const d = team?.draws ?? 0;
  const l = team?.losses ?? 0;
  const label = variant === "tag" ? (team?.tag || "TBD") : (team?.name || team?.tag || "TBD");
  const threadUrl = threadId && guildId ? `https://discord.com/channels/${guildId}/${threadId}` : null;
  return (
    <span
      className="relative inline-flex items-center gap-1"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`opponent-chip-${team?.tag || "tbd"}`}
    >
      <span
        className="underline decoration-dotted decoration-gray-600 underline-offset-4 cursor-help"
        tabIndex={0}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {label}
      </span>
      {threadUrl && (
        <a
          href={threadUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`opponent-thread-chip-${team?.tag || "tbd"}`}
          onClick={(e) => e.stopPropagation()}
          title="Open the match thread in Discord"
          className="text-[9px] font-mono uppercase tracking-widest border border-[#5865F2] text-[#5865F2] hover:bg-[#5865F2] hover:text-white px-1.5 py-0.5 ml-1"
        >
          ↗ thread
        </a>
      )}
      {open && team && (
        <span
          role="tooltip"
          data-testid={`opponent-hover-card-${team.tag}`}
          className="absolute left-0 top-full mt-1 z-50 w-56 border border-[#232330] bg-[#05050A] p-3 shadow-xl text-left pointer-events-none"
          style={{ borderLeft: `3px solid ${team.logo_color || "#5865F2"}` }}
        >
          <div className="flex items-center gap-2 mb-2">
            {team.logo_url ? (
              <img src={team.logo_url} alt="" className="w-8 h-8 object-cover border border-[#232330]" />
            ) : (
              <div
                className="w-8 h-8 flex items-center justify-center text-[10px] font-mono font-bold border border-[#232330]"
                style={{ color: team.logo_color || "#F4D03F" }}
              >
                {team.tag}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold uppercase tracking-tight text-sm truncate">{team.name || team.tag}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500 truncate">
                {team.tag} · {division?.name || "no division"}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1 text-center text-[10px] font-mono uppercase tracking-widest border-t border-[#232330] pt-2">
            <div><div className="text-cyan-400 font-bold text-sm">{w}</div><div className="text-gray-500">W</div></div>
            <div><div className="text-gray-400 font-bold text-sm">{d}</div><div className="text-gray-500">D</div></div>
            <div><div className="text-[#FF007F] font-bold text-sm">{l}</div><div className="text-gray-500">L</div></div>
          </div>
          <div className="text-center text-[9px] font-mono uppercase tracking-widest text-[#F4D03F] mt-2">
            {team.points ?? 0} pts
          </div>
        </span>
      )}
    </span>
  );
}


export default function RepsToolkit() {
  const { user } = useAuth();
  const isAuthed = user && user !== false && typeof user === "object";
  const [teams, setTeams] = useState([]);
  // Iter65 · Track "my teams" separately from the merged all-teams pool so
  // fixture filters remain scoped to teams the current rep actually manages.
  const [myTeams, setMyTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  // Iter66 · Divisions + guild_id power the opponent hover card + thread deep-link chip
  const [divisionsById, setDivisionsById] = useState({});
  const [guildId, setGuildId] = useState("");
  const [chatFor, setChatFor] = useState(null);
  const [scoreFor, setScoreFor] = useState(null);
  const [hs, setHs] = useState(0);
  const [as, setAs] = useState(0);
  const [mvp, setMvp] = useState("");
  const [players, setPlayers] = useState([]);
  const [pullMode, setPullMode] = useState("off"); // off | simulated | rcon_paste | csv
  const [statsCsv, setStatsCsv] = useState("");
  const [statsUrl, setStatsUrl] = useState("");
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [urlPreset, setUrlPreset] = useState("custom");
  const [presetMatchId, setPresetMatchId] = useState("");
  const [statsSuggestions, setStatsSuggestions] = useState(null); // { badhandle: [{handle,score}] }
  const [mismatchFor, setMismatchFor] = useState(null); // { fixture, proposed, mine } — offer dispute
  const [tossFor, setTossFor] = useState(null);
  const [tossFaction, setTossFaction] = useState("allies");
  // Iter30: live HLL stat tracker
  const [liveFor, setLiveFor] = useState(null);
  const [liveRows, setLiveRows] = useState([]);
  const [liveFallback, setLiveFallback] = useState(null);
  const [livePulledAt, setLivePulledAt] = useState(null);
  const [liveAutoRefresh, setLiveAutoRefresh] = useState(true);

  const refreshLive = async (fixture) => {
    const f = fixture || liveFor;
    if (!f) return;
    try {
      const { data } = await api.post(`/fixtures/${f.id}/hll-live-stats-preview`);
      setLiveRows(data.rows || []);
      setLiveFallback(data.fallback || null);
      setLivePulledAt(data.pulled_at || null);
    } catch (e) {
      setLiveFallback(e.response?.data?.detail || "failed");
    }
  };

  // Iter34: auto-refresh Live HLL every 15s while the dialog is open
  useEffect(() => {
    if (!liveFor || !liveAutoRefresh) return;
    const t = setInterval(() => refreshLive(liveFor), 15000);
    return () => clearInterval(t);
  }, [liveFor, liveAutoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const [filingDispute, setFilingDispute] = useState(false);
  // Iter37: broadcast coin-toss ceremony
  const [flipFor, setFlipFor] = useState(null);
  const [flipPhase, setFlipPhase] = useState("idle"); // idle | bumper | spinning | reveal
  const [flipWinner, setFlipWinner] = useState(null); // "home" | "away"
  const [flipTicker, setFlipTicker] = useState(null); // rotating side pick during spin

  // Iter32: Match Planner (mutual sign-off before fixture lands on the Schedule)
  const [allTeams, setAllTeams] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [proposals, setProposals] = useState({ sent: [], received: [] });
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [planner, setPlanner] = useState({ from_team_id: "", to_team_id: "", scheduled_at: "", map: "Wheelspin", division_id: "", note: "" });
  const [counterFor, setCounterFor] = useState(null);
  const [counter, setCounter] = useState({ scheduled_at: "", map: "", note: "" });
  // Iter33: Match Planner chat
  const [chatProposal, setChatProposal] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const loadProposalChat = async (p) => {
    setChatLoading(true);
    try {
      const { data } = await api.get(`/match-proposals/${p.id}/chat`);
      setChatMessages(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load thread");
    } finally { setChatLoading(false); }
  };

  const openProposalChat = async (p) => {
    setChatProposal(p);
    setChatMessages([]);
    setChatDraft("");
    await loadProposalChat(p);
  };

  const sendProposalChat = async () => {
    if (!chatProposal || !chatDraft.trim()) return;
    try {
      await api.post(`/match-proposals/${chatProposal.id}/chat`, { message: chatDraft.trim() });
      setChatDraft("");
      await loadProposalChat(chatProposal);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  useEffect(() => {
    if (!chatProposal) return;
    const t = setInterval(() => loadProposalChat(chatProposal), 6000);
    return () => clearInterval(t);
  }, [chatProposal]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlanner = async () => {
    try {
      const [t, d, p] = await Promise.all([
        api.get("/teams"),
        api.get("/divisions"),
        api.get("/me/match-proposals"),
      ]);
      setAllTeams(t.data || []);
      setDivisions(d.data || []);
      setProposals(p.data || { sent: [], received: [] });
    } catch {}
  };
  useEffect(() => { if (isAuthed) loadPlanner(); }, [isAuthed]);

  const startCoinCeremony = async (f) => {
    if (flipFor) return;
    const h = teamById(f.home_team_id) || { tag: "HOME" };
    const a = teamById(f.away_team_id) || { tag: "AWAY" };
    setFlipFor(f);
    setFlipWinner(null);
    setFlipPhase("bumper");
    // Bumper 3s
    await new Promise((r) => setTimeout(r, 3000));
    setFlipPhase("spinning");
    // Kick the API call in parallel with the spin animation
    const apiPromise = api.post(`/fixtures/${f.id}/coin-toss`).catch((e) => ({ error: e }));
    // 3s spin with side flicker every 90ms
    const tickerId = setInterval(() => {
      setFlipTicker(Math.random() < 0.5 ? h.tag : a.tag);
    }, 90);
    await new Promise((r) => setTimeout(r, 3000));
    clearInterval(tickerId);
    setFlipTicker(null);
    const resp = await apiPromise;
    if (resp?.error) {
      toast.error(resp.error.response?.data?.detail || "Failed");
      setFlipFor(null); setFlipPhase("idle");
      return;
    }
    const winner = resp.data?.coin_toss?.winner;
    setFlipWinner(winner);
    setFlipPhase("reveal");
    // Reveal hold 4s (caster-friendly)
    setTimeout(async () => {
      toast.success(`${winner === "home" ? h.tag : a.tag} won the toss!`);
      setFlipFor(null); setFlipPhase("idle"); setFlipWinner(null);
      load();
    }, 4000);
  };

  const load = async () => {
    if (!isAuthed) return;
    const [mt, fx, ta, divs, invite] = await Promise.all([
      api.get("/me/teams"),
      api.get("/fixtures"),
      api.get("/teams"),
      api.get("/divisions").catch(() => ({ data: [] })),
      api.get("/community/discord-invite").catch(() => ({ data: {} })),
    ]);
    // Iter65 · Merge my teams + full roster so opponent tags render instead of
    // falling back to "TBD" in fixture cards. My teams take precedence so
    // ownership-scoped fields (channel_id etc.) win the merge.
    const myList = mt.data || [];
    const myTeamsById = new Map(myList.map((t) => [t.id, t]));
    const merged = [
      ...myList,
      ...((ta.data || []).filter((t) => !myTeamsById.has(t.id))),
    ];
    setMyTeams(myList);
    setTeams(merged);
    setFixtures(fx.data || []);
    // Iter66 · Build divisionsById lookup + stash guild_id for thread links
    const divMap = {};
    (divs.data || []).forEach((d) => { divMap[d.id] = d; });
    setDivisionsById(divMap);
    setGuildId(invite.data?.guild_id || "");
    // Load roster players for MVP dropdown (my teams only)
    if ((mt.data || []).length) {
      const rosters = await Promise.all(
        (mt.data || []).map((t) => api.get(`/players?team_id=${t.id}`).then((r) => r.data).catch(() => []))
      );
      setPlayers(rosters.flat());
    }
  };
  useEffect(() => { load(); }, [isAuthed]);

  const myTeamIds = useMemo(() => new Set(myTeams.map((t) => t.id)), [myTeams]);
  const teamById = (id) => teams.find((t) => t.id === id);
  // Iter78 · Admins + league officials get the full upcoming slate so they can
  // press the wheel + ready buttons on behalf of either team.
  const isPrivileged = user?.role === "admin" || user?.role === "official";
  const myFixtures = useMemo(() => fixtures
    .filter((f) => f.status !== "completed" && (isPrivileged || myTeamIds.has(f.home_team_id) || myTeamIds.has(f.away_team_id)))
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")),
    [fixtures, myTeamIds, isPrivileged]);

  // Iter45 · Recently completed fixtures for the Stats Upload flow (last 30 days).
  const myCompletedFixtures = useMemo(() => {
    const cutoff = Date.now() - 30 * 86_400_000;
    return fixtures
      .filter((f) => f.status === "completed" && (myTeamIds.has(f.home_team_id) || myTeamIds.has(f.away_team_id)))
      .filter((f) => new Date(f.completed_at || f.scheduled_at || 0).getTime() > cutoff)
      .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
  }, [fixtures, myTeamIds]);

  const mySideFor = (f) => myTeamIds.has(f.home_team_id) ? "home" : (myTeamIds.has(f.away_team_id) ? "away" : null);

  if (!user) return <div className="text-gray-500">Loading…</div>;
  if (!isAuthed) return <Navigate to="/login" replace />;

  // Iter45 · Stats CSV upload for a completed fixture.
  const uploadStats = async (f, fileObj) => {
    if (!fileObj) return;
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await apiUpload(`/fixtures/${f.id}/stats-upload`, fd);
      const unmatched = data.unmatched_handles || [];
      const suggestions = data.suggestions || {};
      let msg = `Stats saved · ${data.updated_count} player${data.updated_count === 1 ? "" : "s"} updated`;
      if (unmatched.length) {
        const hintLines = unmatched.slice(0, 5).map((h) => {
          const s = (suggestions[h] || [])[0];
          return s ? `${h} → ${s.handle}?` : h;
        });
        msg += ` · ${unmatched.length} unmatched (${hintLines.join(", ")})`;
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const markReady = async (f, forcedSide = null) => {
    try {
      const side = forcedSide || mySideFor(f);
      const { data } = await api.post(`/fixtures/${f.id}/ready`, null, { params: side ? { side } : {} });
      // Iter36 · When my ready is the second one, backend auto-flips the coin.
      // Show the winner instantly so the caller can go straight to the reveal.
      if (data?.coin_toss?.auto && !f.coin_toss) {
        const h = teamById(f.home_team_id) || { tag: "HOME" };
        const a = teamById(f.away_team_id) || { tag: "AWAY" };
        const winnerTag = data.coin_toss.winner === "home" ? h.tag : a.tag;
        toast.success(`Both teams ready — coin flipped, ${winnerTag} won the toss!`);
      } else {
        toast.success(forcedSide ? `${forcedSide.toUpperCase()} marked ready.` : "Marked ready.");
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ---------- Match Planner handlers ----------
  const submitProposal = async () => {
    if (!planner.from_team_id || !planner.to_team_id || !planner.scheduled_at) {
      toast.error("Your team, opponent & kickoff are required."); return;
    }
    if (planner.from_team_id === planner.to_team_id) {
      toast.error("Opponent must be a different team."); return;
    }
    try {
      const body = {
        from_team_id: planner.from_team_id,
        to_team_id: planner.to_team_id,
        scheduled_at: new Date(planner.scheduled_at).toISOString(),
        map: planner.map || "Wheelspin",
        note: planner.note || "",
      };
      if (planner.division_id) body.division_id = planner.division_id;
      await api.post("/match-proposals", body);
      toast.success("Proposal sent — waiting on the opponent to sign off.");
      setPlannerOpen(false);
      setPlanner({ from_team_id: "", to_team_id: "", scheduled_at: "", map: "Wheelspin", division_id: "", note: "" });
      loadPlanner();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const acceptProposal = async (p) => {
    try {
      await api.post(`/match-proposals/${p.id}/accept`);
      toast.success("Signed off — fixture is live on the Schedule.");
      loadPlanner(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const declineProposal = async (p) => {
    try {
      await api.post(`/match-proposals/${p.id}/decline`);
      toast.success("Proposal declined.");
      loadPlanner();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancelProposal = async (p) => {
    try {
      await api.delete(`/match-proposals/${p.id}`);
      toast.success("Proposal withdrawn.");
      loadPlanner();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitCounter = async () => {
    if (!counterFor || !counter.scheduled_at) return;
    try {
      const body = { scheduled_at: new Date(counter.scheduled_at).toISOString() };
      if (counter.map) body.map = counter.map;
      if (counter.note) body.note = counter.note;
      await api.post(`/match-proposals/${counterFor.id}/counter`, body);
      toast.success("Counter-proposal sent.");
      setCounterFor(null);
      setCounter({ scheduled_at: "", map: "", note: "" });
      loadPlanner();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const tById = (id) => allTeams.find((t) => t.id === id);

  const openScore = (f) => {
    setScoreFor(f);
    setHs(f.home_score ?? 0);
    setAs(f.away_score ?? 0);
    setMvp(f.mvp_player_id || "");
    setPullMode("off");
    setStatsCsv("");
  };

  const submitScore = async () => {
    if (!scoreFor) return;
    if (Number(hs) < 0 || Number(as) < 0) { toast.error("Scores must be positive."); return; }
    try {
      const payload = { home_score: Number(hs), away_score: Number(as) };
      if (mvp) payload.mvp_player_id = mvp;
      if (pullMode !== "off") {
        payload.pull_server_stats = pullMode;
        if (pullMode === "csv") payload.server_stats_csv = statsCsv;
      }
      // Iter81 · Admin/official editing an already-completed result uses PUT
      // so we hit the dedicated edit endpoint (reverses the old effect and
      // re-applies the new score through the finalize path).
      const isEdit = scoreFor.status === "completed" && (user?.role === "admin" || user?.role === "official");
      const { data } = await (isEdit
        ? api.put(`/fixtures/${scoreFor.id}/result`, payload)
        : api.post(`/fixtures/${scoreFor.id}/result`, payload));
      if (isEdit) {
        toast.success(`Result updated · ${data?.previous?.home_score ?? "?"}–${data?.previous?.away_score ?? "?"} → ${data?.new?.home_score}–${data?.new?.away_score}`);
        setScoreFor(null); setHs(0); setAs(0); setMvp("");
        setPullMode("off"); setStatsCsv("");
        load();
        return;
      }
      // Iter33: surface handle suggestions for any unmatched RCON handles
      if (data?.stats_import?.suggestions && Object.keys(data.stats_import.suggestions).length > 0) {
        setStatsSuggestions(data.stats_import.suggestions);
      } else {
        setStatsSuggestions(null);
      }
      // Iter25: rep-signature workflow
      if (data?.status === "pending_signatures") {
        toast.success(`Sheet signed — waiting on ${data.waiting_on?.toUpperCase()} rep to countersign.`);
        setScoreFor(null); setHs(0); setAs(0); setMvp("");
        setPullMode("off"); setStatsCsv("");
        load();
      } else if (data?.status === "signature_mismatch") {
        // Iter27: offer one-click dispute flow
        setMismatchFor({
          fixture: scoreFor,
          proposed: data.proposed,
          mine: data.you_submitted,
        });
        // Keep the score dialog open behind so state persists — the mismatch modal takes over
      } else {
        const imp = data?.stats_import;
        if (imp) {
          const note = imp.fallback ? ` · ${imp.fallback}` : "";
          toast.success(`Result locked — pulled ${imp.updated_count} player stats (${imp.source})${note}${imp.unmatched_handles?.length ? ` · ${imp.unmatched_handles.length} unmatched` : ""}.`);
        } else {
          toast.success("Result locked — standings auto-updated.");
        }
        setScoreFor(null); setHs(0); setAs(0); setMvp("");
        setPullMode("off"); setStatsCsv("");
        load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const fileDisputeFromMismatch = async () => {
    if (!mismatchFor) return;
    setFilingDispute(true);
    try {
      const { fixture, proposed, mine } = mismatchFor;
      const h = teamById(fixture.home_team_id) || { tag: "HOME" };
      const a = teamById(fixture.away_team_id) || { tag: "AWAY" };
      await api.post("/disputes", {
        fixture_id: fixture.id,
        category: "score_dispute",
        title: `Score mismatch: ${h.tag} vs ${a.tag}`,
        body:
          `Reps submitted different final scores.\n` +
          `Other rep proposed: ${h.tag} ${proposed?.home_score ?? "?"} — ${a.tag} ${proposed?.away_score ?? "?"}\n` +
          `I submitted: ${h.tag} ${mine?.home_score ?? "?"} — ${a.tag} ${mine?.away_score ?? "?"}\n` +
          `Please review and issue an official final score.`,
      });
      toast.success("Dispute filed — officials will step in.");
      setMismatchFor(null);
      setScoreFor(null); setHs(0); setAs(0); setMvp("");
      setPullMode("off"); setStatsCsv("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to file dispute");
    } finally {
      setFilingDispute(false);
    }
  };

  const uploadLogo = async (teamId, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image."); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      await apiUpload(`/teams/${teamId}/logo`, fd);
      toast.success("Team logo updated.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadCsv = async (teamId, file) => {
    if (!file) return;
    const fn = file.name.toLowerCase();
    if (!(fn.endsWith(".csv") || fn.endsWith(".cvc") || fn.endsWith(".pdf"))) { toast.error("Pick a CSV, CVC or PDF."); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      await apiUpload(`/teams/${teamId}/roster-csv`, fd);
      toast.success("Roster uploaded.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (myTeams.length === 0 && !isPrivileged) {
    return (
      <div className="space-y-6" data-testid="reps-root">
        <div>
          <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
            <UsersIcon className="w-3.5 h-3.5" /> / REPS TOOLKIT
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Team Reps</h1>
        </div>
        <div className="border border-[#232330] p-8 text-center text-gray-500">
          You don't own or rep a team yet. Register one on the{" "}
          <Link to="/my-team" className="text-cyan-400 hover:underline">My Team</Link> page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="reps-root">
      <div>
        <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
          <UsersIcon className="w-3.5 h-3.5" /> / REPS TOOLKIT
        </div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Team Reps</h1>
        <p className="text-gray-400 mt-2 text-sm max-w-2xl">
          Everything you need on match day: ready toggle, private chat with officials & the other side, and roster CSV — in one view.
        </p>
        {isPrivileged && (
          <div
            className="mt-4 border border-[#F4D03F]/40 bg-[#F4D03F]/10 px-4 py-2.5 text-[11px] font-mono uppercase tracking-widest text-[#F4D03F] inline-flex items-center gap-2"
            data-testid="reps-admin-override-banner"
          >
            <UsersIcon className="w-3.5 h-3.5" />
            Admin override active — you can ready any team on any fixture and spin any wheel.
          </div>
        )}
      </div>

      {/* Per-team roster + logo tools */}
      <section data-testid="reps-teams" className="grid gap-3 md:grid-cols-2">
        {myTeams.map((t) => (
          <div key={t.id} data-testid={`reps-team-${t.tag}`} className="border border-[#232330] bg-[#101015] p-4" style={{ borderLeft: `4px solid ${t.logo_color}` }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                  {t.status === "approved" ? "APPROVED" : (t.status || "PENDING").toUpperCase()} · {t.tier || "Mains"}
                </div>
                <div className="font-heading text-2xl font-black uppercase tracking-tight" style={{ color: t.logo_color }}>
                  {t.name}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">{t.tag}</div>
              </div>
              <div className="text-right">
                <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500">PTS</div>
                <div className="font-heading font-black text-2xl text-cyan-400">{t.points || 0}</div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <label
                data-testid={`reps-upload-logo-${t.tag}`}
                className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-cyan-400 border border-[#232330] hover:border-cyan-400 px-3 py-1.5 flex items-center gap-1"
              >
                <Upload className="w-3 h-3" /> Logo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(t.id, e.target.files?.[0])} />
              </label>
              <label
                data-testid={`reps-upload-csv-${t.tag}`}
                className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#F4D03F] border border-[#232330] hover:border-[#F4D03F] px-3 py-1.5 flex items-center gap-1"
              >
                <FileSpreadsheet className="w-3 h-3" /> {t.roster_csv_name ? "Replace CSV" : "Upload roster CSV"}
                <input type="file" accept=".csv,.cvc,text/csv,.pdf,application/pdf" className="hidden" onChange={(e) => uploadCsv(t.id, e.target.files?.[0])} />
              </label>
              <a
                href={`${API}/teams/${t.id}/roster.cvc`}
                data-testid={`reps-export-cvc-${t.tag}`}
                className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400/40 hover:border-cyan-400 hover:bg-cyan-400/10 px-3 py-1.5 flex items-center gap-1"
                title="Download the league CVC roster file"
              >
                <Download className="w-3 h-3" /> Export CVC
              </a>
              {t.roster_csv_id && (
                <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border border-[#232330] px-3 py-1.5">
                  CSV: {t.roster_csv_name}
                </span>
              )}
            </div>
          </div>
        ))}
      </section>

      {/* Match Planner is deprecated — the League Office now drives round scheduling.
          Reps continue negotiating time/fielding inside the match chat on each round fixture. */}
      {false && (
      <section data-testid="reps-match-planner" className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <CalendarPlus className="w-4 h-4 text-[#00FFB2]" />
            <div className="font-heading text-xl font-black uppercase tracking-tight">Match Planner</div>
            {(proposals.received?.length || 0) > 0 && (
              <span data-testid="planner-received-count" className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-[#00FFB2] text-[#00FFB2] bg-[#00FFB2]/10 animate-pulse">
                {proposals.received.length} awaiting you
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadPlanner}
              data-testid="planner-refresh"
              className="text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-white border border-[#232330] hover:border-white px-3 py-1.5 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <button
              onClick={() => setPlannerOpen((v) => !v)}
              data-testid="planner-propose-open"
              className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#00FFB2] hover:bg-emerald-300 px-3 py-1.5 flex items-center gap-1 font-bold"
            >
              <Send className="w-3 h-3" /> {plannerOpen ? "Close proposer" : "Propose a match"}
            </button>
          </div>
        </div>
        <p className="text-[11px] font-mono uppercase tracking-widest text-gray-500">
          Reps agree on date & map. Once both teams sign off, the fixture auto-posts to the Schedule.
        </p>

        {plannerOpen && (
          <div className="border border-[#00FFB2]/60 bg-[#101015] p-4 grid gap-2 sm:grid-cols-6 items-end" data-testid="planner-form">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Your team</Label>
              <select
                value={planner.from_team_id}
                onChange={(e) => setPlanner({ ...planner, from_team_id: e.target.value })}
                data-testid="planner-from-team"
                className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              >
                <option value="">— pick —</option>
                {myTeams.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Opponent</Label>
              <select
                value={planner.to_team_id}
                onChange={(e) => setPlanner({ ...planner, to_team_id: e.target.value })}
                data-testid="planner-to-team"
                className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              >
                <option value="">— pick —</option>
                {allTeams.filter((t) => t.id !== planner.from_team_id).map((t) => <option key={t.id} value={t.id}>{t.tag} — {t.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Division</Label>
              <select
                value={planner.division_id}
                onChange={(e) => setPlanner({ ...planner, division_id: e.target.value })}
                data-testid="planner-division"
                className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              >
                <option value="">— none —</option>
                {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Kickoff (UTC)</Label>
              <Input
                type="datetime-local"
                value={planner.scheduled_at}
                onChange={(e) => setPlanner({ ...planner, scheduled_at: e.target.value })}
                data-testid="planner-when"
                className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Map</Label>
              <select
                value={planner.map}
                onChange={(e) => setPlanner({ ...planner, map: e.target.value })}
                data-testid="planner-map"
                className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              >
                {["Wheelspin","Stalingrad","Kursk","Kharkov","El Alamein","Foy","Hurtgen Forest","Utah Beach","Omaha Beach","Sainte-Mère-Église","Sainte-Marie-du-Mont","Carentan","Purple Heart Lane","Hill 400","Driel","Remagen","Mortain","Elsenborn Ridge"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="sm:col-span-6">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Note (optional)</Label>
              <Input
                placeholder="Anything the other rep should know..."
                value={planner.note}
                onChange={(e) => setPlanner({ ...planner, note: e.target.value })}
                data-testid="planner-note"
                className="rounded-none bg-[#05050A] border-[#232330] mt-1 text-sm"
              />
            </div>
            <div className="sm:col-span-6 flex gap-2">
              <Button
                onClick={submitProposal}
                data-testid="planner-submit"
                className="rounded-none bg-[#00FFB2] hover:bg-emerald-300 text-black uppercase tracking-widest text-xs font-bold"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Send proposal
              </Button>
              <Button
                onClick={() => setPlannerOpen(false)}
                variant="outline"
                className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs"
              >
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Received — needs my sign-off */}
          <div className="border border-[#232330] bg-[#0A0A10] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FFB2] mb-2">Awaiting your sign-off</div>
            {(proposals.received || []).length === 0 ? (
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 p-4 text-center">No pending proposals.</div>
            ) : (
              <div className="space-y-2">
                {proposals.received.map((p) => {
                  const from = tById(p.from_team_id);
                  const to = tById(p.to_team_id);
                  const div = divisions.find((d) => d.id === p.division_id);
                  const iOwnCounterSide = (p.last_counter_by === "to");
                  return (
                    <div key={p.id} data-testid={`planner-received-${p.id}`} className="border border-[#00FFB2]/40 bg-[#101015] p-3">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <div className="font-heading font-bold uppercase text-sm">
                          <span style={{ color: from?.logo_color }}>{p.from_team_tag}</span>
                          <span className="text-gray-500 mx-2">→</span>
                          <span style={{ color: to?.logo_color }}>{p.to_team_tag}</span>
                        </div>
                        {p.status === "countered" && (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#F4D03F] text-[#F4D03F] bg-[#F4D03F]/10">
                            {iOwnCounterSide ? "You countered" : "Counter received"}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-gray-400">
                        {new Date(p.scheduled_at).toLocaleString()} · Map: {p.map || "TBD"}
                        {div && <span className="ml-2" style={{ color: div.color }}>· {div.name}</span>}
                      </div>
                      {p.note && (
                        <div className="mt-1 text-[10px] font-mono text-gray-500 italic">"{p.note}"</div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          onClick={() => acceptProposal(p)}
                          data-testid={`planner-accept-${p.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#00FFB2] hover:bg-emerald-300 px-3 py-1.5 flex items-center gap-1 font-bold"
                        >
                          <Check className="w-3 h-3" /> Accept & lock in
                        </button>
                        <button
                          onClick={() => { setCounterFor(p); setCounter({ scheduled_at: p.scheduled_at ? new Date(p.scheduled_at).toISOString().slice(0, 16) : "", map: p.map || "", note: "" }); }}
                          data-testid={`planner-counter-${p.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F] hover:bg-[#F4D03F] hover:text-black px-3 py-1.5 flex items-center gap-1"
                        >
                          Counter-propose
                        </button>
                        <button
                          onClick={() => declineProposal(p)}
                          data-testid={`planner-decline-${p.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500 hover:bg-red-500 hover:text-black px-3 py-1.5 flex items-center gap-1"
                        >
                          <XIcon className="w-3 h-3" /> Decline
                        </button>
                        <button
                          onClick={() => openProposalChat(p)}
                          data-testid={`planner-chat-${p.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF] hover:bg-[#B084FF] hover:text-black px-3 py-1.5 flex items-center gap-1"
                        >
                          <MessageSquare className="w-3 h-3" /> Chat
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sent — waiting on the other side */}
          <div className="border border-[#232330] bg-[#0A0A10] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] mb-2">Waiting on the opponent</div>
            {(proposals.sent || []).length === 0 ? (
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 p-4 text-center">Nothing pending.</div>
            ) : (
              <div className="space-y-2">
                {proposals.sent.map((p) => {
                  const from = tById(p.from_team_id);
                  const to = tById(p.to_team_id);
                  const div = divisions.find((d) => d.id === p.division_id);
                  return (
                    <div key={p.id} data-testid={`planner-sent-${p.id}`} className="border border-[#B084FF]/40 bg-[#101015] p-3">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <div className="font-heading font-bold uppercase text-sm">
                          <span style={{ color: from?.logo_color }}>{p.from_team_tag}</span>
                          <span className="text-gray-500 mx-2">→</span>
                          <span style={{ color: to?.logo_color }}>{p.to_team_tag}</span>
                        </div>
                        <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#B084FF] text-[#B084FF] bg-[#B084FF]/10">
                          {p.status === "countered" ? "Countered" : "Pending"}
                        </span>
                      </div>
                      <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-gray-400">
                        {new Date(p.scheduled_at).toLocaleString()} · Map: {p.map || "TBD"}
                        {div && <span className="ml-2" style={{ color: div.color }}>· {div.name}</span>}
                      </div>
                      {p.note && (
                        <div className="mt-1 text-[10px] font-mono text-gray-500 italic">"{p.note}"</div>
                      )}
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => cancelProposal(p)}
                          data-testid={`planner-cancel-${p.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500 hover:bg-red-500 hover:text-black px-3 py-1.5 flex items-center gap-1"
                        >
                          <XIcon className="w-3 h-3" /> Withdraw
                        </button>
                        <button
                          onClick={() => openProposalChat(p)}
                          data-testid={`planner-chat-sent-${p.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF] hover:bg-[#B084FF] hover:text-black px-3 py-1.5 flex items-center gap-1"
                        >
                          <MessageSquare className="w-3 h-3" /> Chat
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Upcoming fixtures with ready toggle + chat */}
      <section data-testid="reps-fixtures" className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Radio className="w-4 h-4 text-[#B084FF]" />
          <div className="font-heading text-xl font-black uppercase tracking-tight">Your Next Matches</div>
          <span
            data-testid="reps-upcoming-count"
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-[#B084FF] text-[#B084FF] bg-[#B084FF]/10"
          >
            {myFixtures.length} upcoming
          </span>
        </div>
        {myFixtures.length === 0 ? (
          <div className="border border-[#232330] p-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            No scheduled matches for your teams.
          </div>
        ) : (
          <div className="space-y-2">
            {myFixtures.map((f) => {
              const h = teamById(f.home_team_id) || { name: "TBD", tag: "TBD" };
              const a = teamById(f.away_team_id) || { name: "TBD", tag: "TBD" };
              const side = mySideFor(f);
              const iAmReady = side && f.ready?.[side];
              // Iter66 · Pick the opponent side to decorate with hover card + thread chip
              const myIsHome = side === "home";
              const opp = myIsHome ? a : h;
              const oppDiv = divisionsById[opp.division_id];
              return (
                <div
                  key={f.id}
                  data-testid={`reps-fixture-${f.id}`}
                  className="border border-[#232330] bg-[#101015] p-4 flex flex-wrap items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold uppercase tracking-tight truncate">
                      {myIsHome ? (
                        <>
                          {h.name} <span className="text-gray-500">vs</span>{" "}
                          <OpponentChip team={opp} division={oppDiv} threadId={f.discord_thread_id} guildId={guildId} variant="name" />
                        </>
                      ) : (
                        <>
                          <OpponentChip team={opp} division={oppDiv} threadId={f.discord_thread_id} guildId={guildId} variant="name" />
                          {" "}<span className="text-gray-500">vs</span> {a.name}
                        </>
                      )}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                      {f.game}{f.map ? " · " + f.map : ""}{f.wheelspin_map ? " · locked: " + f.wheelspin_map : ""}
                    </div>
                    <div className="mt-2 flex gap-3 flex-wrap items-center">
                      <DualClock iso={f.scheduled_at} />
                      <Countdown iso={f.scheduled_at} size="sm" liveLabel="MATCH LIVE" />
                    </div>
                    <div className="mt-2">
                      <MatchTimeline fixture={f} size="sm" />
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {/* Iter67 · Compact ready-state chips: `whitespace-nowrap` keeps
                          each chip on one line even on mobile, and the glyph (✓ / ○)
                          communicates state without needing to render "NOT READY" text. */}
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${f.ready?.home ? "border-cyan-400 text-cyan-400" : "border-[#232330] text-gray-500"}`}>
                        <span className="font-bold">{h.tag}</span>
                        <span>{f.ready?.home ? "✓" : "○"}</span>
                      </span>
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${f.ready?.away ? "border-[#FF007F] text-[#FF007F]" : "border-[#232330] text-gray-500"}`}>
                        <span className="font-bold">{a.tag}</span>
                        <span>{f.ready?.away ? "✓" : "○"}</span>
                      </span>
                      {/* Iter25 rep signature pills */}
                      {f.rep_signatures?.home && (
                        <span data-testid={`reps-sig-home-${f.id}`} className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#F4D03F] text-[#F4D03F]">
                          {h.tag} SIGNED ✍
                        </span>
                      )}
                      {f.rep_signatures?.away && (
                        <span data-testid={`reps-sig-away-${f.id}`} className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#F4D03F] text-[#F4D03F]">
                          {a.tag} SIGNED ✍
                        </span>
                      )}
                      {f.status === "pending_signatures" && (
                        <span data-testid={`reps-status-pending-${f.id}`} className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#B084FF] text-[#B084FF] bg-[#B084FF]/10">
                          PENDING SIGNOFF
                        </span>
                      )}
                      {f.status === "pending_approval" && (
                        <span data-testid={`reps-status-approval-${f.id}`} className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#FF007F] text-[#FF007F] bg-[#FF007F]/10">
                          AWAITING OFFICIAL APPROVAL
                        </span>
                      )}
                      {/* Iter89 · Fielding gate removed — reps go straight from
                          kickoff + ready → wheelspin. Fielding button + pill
                          hidden; the backend endpoint stays for backwards
                          compatibility on legacy fixtures. */}
                      {/* Iter52 · Time reschedule wizard — visible for both sides on any pre-live fixture */}
                      {side && f.status !== "completed" && f.status !== "live" && (
                        <ReschedulePropose fixture={f} onDone={() => load()} />
                      )}
                      {/* Iter54 · Discord match thread */}
                      {side && f.status !== "completed" && (
                        <MatchThreadButton fixture={f} onDone={() => load()} />
                      )}
                    </div>
                    {side && f.reschedule_proposal?.status === "pending" && (
                      <div className="mt-3">
                        <RescheduleDecision fixture={f} onDone={() => load()} />
                      </div>
                    )}
                    {f.proposed_result && f.status !== "completed" && (
                      <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-gray-400" data-testid={`reps-proposed-${f.id}`}>
                        Proposed: {h.tag} {f.proposed_result.home_score} · {a.tag} {f.proposed_result.away_score}
                      </div>
                    )}
                    {/* Iter35 · Coin toss controls on the rep match */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {side && !f.coin_toss && f.status !== "completed" && (
                        <button
                          onClick={() => startCoinCeremony(f)}
                          disabled={flipFor?.id === f.id}
                          data-testid={`reps-toss-${f.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F] hover:bg-[#F4D03F] hover:text-black px-3 py-1.5 flex items-center gap-1 disabled:opacity-70 disabled:cursor-wait"
                        >
                          <Coins className={`w-3 h-3 ${flipFor?.id === f.id ? "animate-spin" : ""}`} /> {flipFor?.id === f.id ? "Flipping…" : "Flip coin"}
                        </button>
                      )}
                      {f.coin_toss && (
                        <span data-testid={`reps-toss-result-${f.id}`} className="text-[10px] font-mono uppercase tracking-widest border border-[#F4D03F] text-[#F4D03F] bg-[#F4D03F]/10 px-2 py-1 flex items-center gap-1">
                          <Coins className="w-3 h-3" /> Toss · {f.coin_toss.winner === "home" ? h.tag : a.tag}
                          {f.faction_pick_locked ? " · locked" : " · picking"}
                        </span>
                      )}
                      {f.coin_toss && !f.faction_pick_locked && side === f.coin_toss.winner && (
                        <button
                          onClick={() => setTossFor(f)}
                          data-testid={`reps-pick-faction-${f.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-black bg-[#F4D03F] hover:bg-yellow-300 px-3 py-1.5 flex items-center gap-1 font-bold"
                        >
                          Pick faction
                        </button>
                      )}
                      {f.faction_pick_locked && (
                        <span className="text-[10px] font-mono uppercase tracking-widest border border-cyan-400 text-cyan-400 px-2 py-1">
                          {h.tag}·{(f.faction_home || "allies").toUpperCase()} vs {a.tag}·{(f.faction_away || "axis").toUpperCase()}
                        </span>
                      )}
                    </div>
                    {/* Iter35 · Map Wheelspin — inline on the rep match card */}
                    {(f.map || "").toLowerCase() === "wheelspin" && (
                      <div className="mt-3" data-testid={`reps-wheelspin-${f.id}`}>
                        <WheelspinReveal
                          fixture={f}
                          canRoll={!!side || isPrivileged}
                          side={side}
                          canBroadcast={isPrivileged || !!side}
                          isPrivileged={isPrivileged}
                          onAdminReady={(whichSide) => markReady(f, whichSide)}
                          onRolled={() => load()}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {isPrivileged ? (
                      <div className="flex items-center gap-2 flex-wrap" data-testid={`reps-ready-admin-${f.id}`}>
                        <span className="text-[9px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F]/40 bg-[#F4D03F]/10 px-2 py-1">
                          Admin override
                        </span>
                        <button
                          onClick={() => markReady(f, "home")}
                          disabled={!!(f.ready || {}).home}
                          data-testid={`reps-ready-home-${f.id}`}
                          className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Check className="w-3.5 h-3.5" /> {(f.ready || {}).home ? `${h.tag} ✓` : `Ready ${h.tag}`}
                        </button>
                        <button
                          onClick={() => markReady(f, "away")}
                          disabled={!!(f.ready || {}).away}
                          data-testid={`reps-ready-away-${f.id}`}
                          className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Check className="w-3.5 h-3.5" /> {(f.ready || {}).away ? `${a.tag} ✓` : `Ready ${a.tag}`}
                        </button>
                      </div>
                    ) : side ? (
                      <button
                        onClick={() => markReady(f)}
                        disabled={iAmReady}
                        data-testid={`reps-ready-${f.id}`}
                        className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Check className="w-3.5 h-3.5" /> {iAmReady ? "Ready ✓" : `Ready ${side === "home" ? h.tag : a.tag}`}
                      </button>
                    ) : null}
                    <button
                      onClick={() => openScore(f)}
                      data-testid={`reps-score-${f.id}`}
                      className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-1"
                    >
                      <Trophy className="w-3.5 h-3.5" /> {f.status === "completed" ? "Edit score" : "Report score"}
                    </button>
                    <button
                      onClick={() => setChatFor(f)}
                      data-testid={`reps-chat-${f.id}`}
                      className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold px-3 py-2 flex items-center gap-1"
                    >
                      <MessageSquare className="w-3.5 h-3.5" /> Chat
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Iter45 · Recently completed fixtures — Stats Upload flow */}
      {myCompletedFixtures.length > 0 && (
        <section data-testid="reps-completed-fixtures" className="space-y-3">
          <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs">/ RECENTLY COMPLETED</div>
          <div className="text-sm text-gray-400">Upload a match stats CSV to feed the Players page and top-fraggers board. Format: <span className="font-mono text-cyan-400">handle,kills,deaths,assists</span>.</div>
          <div className="border border-[#232330] bg-[#101015] divide-y divide-[#232330]">
            {myCompletedFixtures.map((f) => {
              const h = teamById(f.home_team_id) || { tag: "HOME" };
              const a = teamById(f.away_team_id) || { tag: "AWAY" };
              const uploaded = !!f.stats_imported;
              return (
                <div key={f.id} data-testid={`reps-completed-${f.id}`} className="p-3 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-heading font-black uppercase tracking-tight">
                      {h.tag} <span className="text-white/60 mx-1">{f.home_score ?? 0}—{f.away_score ?? 0}</span> {a.tag}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                      {f.completed_at ? new Date(f.completed_at).toLocaleString() : "—"} · {f.map || "TBD"}
                    </div>
                  </div>
                  {uploaded && (
                    <span
                      data-testid={`reps-stats-uploaded-${f.id}`}
                      className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-400/40 bg-emerald-400/5 px-2 py-1"
                      title={f.stats_imported_at ? `Stats uploaded ${new Date(f.stats_imported_at).toLocaleString()} by ${f.stats_imported_by || "?"} · ${f.stats_updated_count || 0} players updated` : "Stats already uploaded"}
                    >
                      ✓ stats · {f.stats_updated_count || 0} players
                    </span>
                  )}
                  <label
                    data-testid={`reps-stats-upload-${f.id}`}
                    className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold"
                  >
                    <Upload className="w-3 h-3" /> {uploaded ? "Replace stats" : "Upload stats"}
                    <input type="file" accept=".csv,text/csv" className="hidden"
                           onChange={(e) => { const fl = e.target.files?.[0]; e.target.value = ""; uploadStats(f, fl); }} />
                  </label>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <FixtureChat
        fixture={chatFor}
        teams={teams}
        open={!!chatFor}
        onClose={() => setChatFor(null)}
      />

      {/* Iter37: Broadcast Coin Toss Overlay — bumper → spin → reveal (caster-friendly full-screen) */}
      {flipFor && (() => {
        const h = teamById(flipFor.home_team_id) || { tag: "HOME", logo_color: "#00F0FF" };
        const a = teamById(flipFor.away_team_id) || { tag: "AWAY", logo_color: "#FF007F" };
        const winnerTeam = flipWinner === "home" ? h : (flipWinner === "away" ? a : null);
        return (
          <div
            data-testid="coin-flip-broadcast"
            className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center p-6"
          >
            <div className="pointer-events-none absolute inset-0" style={{
              background: "radial-gradient(ellipse at center, rgba(244,208,63,0.25), transparent 60%), radial-gradient(ellipse at bottom, rgba(176,132,255,0.15), transparent 60%)",
            }} />
            <button
              onClick={() => { setFlipFor(null); setFlipPhase("idle"); setFlipWinner(null); setFlipTicker(null); }}
              data-testid="coin-broadcast-close"
              className="absolute top-6 right-6 text-white/60 hover:text-white flex items-center gap-1 text-xs font-mono uppercase tracking-widest z-10"
            >
              Close
            </button>
            <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-4 flex items-center gap-2">
              <Coins className="w-3.5 h-3.5" /> / COIN TOSS LIVE
            </div>
            <div className="font-heading font-black uppercase tracking-tighter text-2xl sm:text-4xl text-white mb-10 text-center">
              <span style={{ color: h.logo_color }}>{h.tag}</span>
              <span className="text-gray-600 mx-3">vs</span>
              <span style={{ color: a.logo_color }}>{a.tag}</span>
            </div>

            {flipPhase === "bumper" && (
              <div className="text-center" data-testid="coin-phase-bumper">
                <div className="text-[#F4D03F] font-mono tracking-[0.4em] text-xs mb-6 uppercase animate-pulse">
                  Coming Up · Coin Toss
                </div>
                <div
                  className="font-heading font-black leading-none uppercase tracking-tighter text-white"
                  style={{ fontSize: "clamp(3rem, 10vw, 9rem)", textShadow: "0 0 60px rgba(244,208,63,0.7)" }}
                >
                  READY
                </div>
              </div>
            )}

            {flipPhase === "spinning" && (
              <div className="flex flex-col items-center gap-8" data-testid="coin-phase-spinning">
                <div
                  className="relative w-56 h-56 sm:w-72 sm:h-72"
                  style={{ perspective: "1200px" }}
                >
                  <div
                    className="absolute inset-0 rounded-full border-4 border-[#F4D03F] flex items-center justify-center font-heading font-black text-6xl text-black shadow-[0_0_90px_rgba(244,208,63,0.9)]"
                    style={{
                      background: "radial-gradient(circle at 30% 30%, #FFEB99, #F4D03F 60%, #b8890a 100%)",
                      animation: "coin-broadcast-flip 0.28s linear infinite",
                    }}
                  >
                    L
                  </div>
                  <style>{`
                    @keyframes coin-broadcast-flip {
                      0%   { transform: rotateY(0deg)   scale(1); }
                      50%  { transform: rotateY(180deg) scale(1.15); }
                      100% { transform: rotateY(360deg) scale(1); }
                    }
                  `}</style>
                </div>
                <div className="font-heading font-black uppercase tracking-tighter text-white text-2xl sm:text-4xl">
                  {flipTicker || "…"}
                </div>
                <div className="text-white/50 font-mono text-xs uppercase tracking-[0.3em]">Fate is deciding…</div>
              </div>
            )}

            {flipPhase === "reveal" && winnerTeam && (
              <div className="flex flex-col items-center animate-in fade-in zoom-in duration-500" data-testid="coin-phase-reveal">
                <div className="text-[#F4D03F] font-mono tracking-[0.4em] text-xs mb-4 uppercase">The Toss Winner</div>
                <div
                  className="font-heading font-black leading-none uppercase tracking-tighter text-center"
                  style={{
                    color: winnerTeam.logo_color || "#F4D03F",
                    fontSize: "clamp(4rem, 14vw, 12rem)",
                    textShadow: `0 0 80px ${winnerTeam.logo_color || "#F4D03F"}`,
                  }}
                >
                  {winnerTeam.tag}
                </div>
                <div className="mt-6 text-white/80 font-heading uppercase tracking-tight text-xl">
                  Picks the faction · other side flips
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Iter27: Signature-mismatch → one-click dispute */}
      <Dialog open={!!mismatchFor} onOpenChange={(o) => !o && setMismatchFor(null)}>
        <DialogContent className="bg-[#101015] border-[#FF007F] text-white rounded-none" data-testid="mismatch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-[#FF007F]">
              <ShieldAlert className="w-4 h-4" /> Score mismatch — escalate?
            </DialogTitle>
          </DialogHeader>
          {mismatchFor && (() => {
            const h = teamById(mismatchFor.fixture.home_team_id) || { tag: "HOME" };
            const a = teamById(mismatchFor.fixture.away_team_id) || { tag: "AWAY" };
            return (
              <div className="space-y-3 text-sm">
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
                  The other rep already signed a different final score. Neither result will be locked until both sides match or officials step in.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-[#B084FF] bg-[#B084FF]/10 p-3" data-testid="mismatch-proposed">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF]">Other rep signed</div>
                    <div className="font-mono text-xl mt-1">{h.tag} {mismatchFor.proposed?.home_score} · {mismatchFor.proposed?.away_score} {a.tag}</div>
                  </div>
                  <div className="border border-[#FF007F] bg-[#FF007F]/10 p-3" data-testid="mismatch-mine">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F]">You submitted</div>
                    <div className="font-mono text-xl mt-1">{h.tag} {mismatchFor.mine?.home_score} · {mismatchFor.mine?.away_score} {a.tag}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setMismatchFor(null)}
                    data-testid="mismatch-close"
                    className="rounded-none border-[#232330] hover:border-white uppercase tracking-widest text-xs"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={fileDisputeFromMismatch}
                    disabled={filingDispute}
                    data-testid="mismatch-file-dispute"
                    className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold"
                  >
                    <ShieldAlert className="w-3.5 h-3.5 mr-1" /> {filingDispute ? "Filing…" : "Open dispute"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Automated score report dialog */}
      <Dialog open={!!scoreFor} onOpenChange={(o) => !o && setScoreFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#F4D03F]" /> Report final score
            </DialogTitle>
          </DialogHeader>
          {scoreFor && (
            <div className="space-y-3" data-testid="reps-score-dialog">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Once you submit, standings auto-update and the fixture is marked complete.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">
                    {teamById(scoreFor.home_team_id)?.tag || "HOME"}
                  </Label>
                  <Input
                    type="number" min={0}
                    value={hs}
                    onChange={(e) => setHs(e.target.value)}
                    data-testid="reps-score-home"
                    className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-2xl h-16 text-center"
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F]">
                    {teamById(scoreFor.away_team_id)?.tag || "AWAY"}
                  </Label>
                  <Input
                    type="number" min={0}
                    value={as}
                    onChange={(e) => setAs(e.target.value)}
                    data-testid="reps-score-away"
                    className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-2xl h-16 text-center"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">MVP (optional)</Label>
                <Select value={mvp || "none"} onValueChange={(v) => setMvp(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="reps-score-mvp" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                    <SelectValue placeholder="— pick MVP —" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#101015] border-[#232330] text-white">
                    <SelectItem value="none">— none —</SelectItem>
                    {players
                      .filter((p) => p.team_id === scoreFor.home_team_id || p.team_id === scoreFor.away_team_id)
                      .map((p) => <SelectItem key={p.id} value={p.id}>{p.handle}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Auto-import player stats</Label>
                <div className="grid grid-cols-4 gap-2 mt-2" data-testid="reps-pull-mode-group">
                  {[
                    { v: "off", label: "Off", color: "#232330" },
                    { v: "simulated", label: "Server sim", color: "#B084FF" },
                    { v: "rcon_paste", label: "Paste RCON", color: "#00FFB2" },
                    { v: "csv", label: "Paste CSV", color: "#F4D03F" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setPullMode(opt.v)}
                      data-testid={`reps-pull-mode-${opt.v}`}
                      className={`border py-2 text-[10px] font-mono uppercase tracking-widest transition-colors ${
                        pullMode === opt.v
                          ? "text-black font-bold"
                          : "text-gray-500 hover:text-white"
                      }`}
                      style={pullMode === opt.v ? { background: opt.color, borderColor: opt.color } : { borderColor: "#232330" }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {pullMode === "simulated" && (
                  <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#B084FF]" data-testid="reps-pull-note-sim">
                    · MOCKED: generates a plausible K/D/A delta for every rostered player.
                  </p>
                )}
                {pullMode === "rcon_paste" && (
                  <>
                    <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#00FFB2]" data-testid="reps-pull-note-rcon-paste">
                      · Paste the raw kill log OR drop in an HLL Stats / Frostbite URL — we parse every <span className="text-white">KILL:</span> line and apply K/D deltas.
                    </p>
                    {/* Iter33 · URL provider presets */}
                    <div className="mt-2 flex gap-1 flex-wrap" data-testid="reps-url-presets">
                      {[
                        { v: "custom",    label: "Custom URL",   tpl: "" },
                        { v: "frostbite", label: "Frostbite",    tpl: "https://frostbite.gg/matches/{id}" },
                        { v: "hll_stats", label: "HLL Stats",    tpl: "https://hll-stats.example.com/api/matches/{id}" },
                        { v: "crcon",     label: "CRCON",        tpl: "https://your-crcon.example.com/api/get_recent_logs?filter_action=KILL&match_id={id}" },
                      ].map((p) => (
                        <button
                          key={p.v}
                          type="button"
                          onClick={() => {
                            setUrlPreset(p.v);
                            if (p.tpl && presetMatchId) setStatsUrl(p.tpl.replace("{id}", presetMatchId));
                            else if (!p.tpl) setStatsUrl("");
                          }}
                          data-testid={`reps-url-preset-${p.v}`}
                          className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${
                            urlPreset === p.v ? "border-[#00FFB2] bg-[#00FFB2]/15 text-[#00FFB2]" : "border-[#232330] text-gray-500 hover:text-white"
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    {urlPreset !== "custom" && (
                      <Input
                        value={presetMatchId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setPresetMatchId(id);
                          const tpl = {
                            frostbite: "https://frostbite.gg/matches/{id}",
                            hll_stats: "https://hll-stats.example.com/api/matches/{id}",
                            crcon:     "https://your-crcon.example.com/api/get_recent_logs?filter_action=KILL&match_id={id}",
                          }[urlPreset];
                          if (tpl) setStatsUrl(tpl.replace("{id}", id));
                        }}
                        placeholder="Match ID"
                        data-testid="reps-url-preset-id"
                        className="mt-2 rounded-none bg-[#05050A] border-[#00FFB2]/40 font-mono text-xs"
                      />
                    )}
                    <div className="mt-2 flex gap-2">
                      <Input
                        value={statsUrl}
                        onChange={(e) => setStatsUrl(e.target.value)}
                        placeholder="https://…"
                        data-testid="reps-pull-stats-url"
                        className="rounded-none bg-[#05050A] border-[#00FFB2]/40 font-mono text-xs flex-1"
                      />
                      <Button
                        type="button"
                        onClick={async () => {
                          if (!statsUrl.trim()) { toast.error("Paste a stats URL first"); return; }
                          setFetchingUrl(true);
                          try {
                            const { data } = await api.post("/stats/fetch-url", { url: statsUrl });
                            const log = data.parsed_log || data.raw || "";
                            if (!log.trim()) { toast.error("URL returned nothing to parse"); return; }
                            setStatsCsv(log);
                            toast.success(data.parsed_log ? "Structured stats parsed into kill log" : "Raw response pasted below");
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Fetch failed");
                          } finally { setFetchingUrl(false); }
                        }}
                        disabled={fetchingUrl}
                        data-testid="reps-pull-stats-fetch"
                        className="rounded-none bg-[#00FFB2] hover:bg-emerald-300 text-black uppercase tracking-widest text-[10px] font-bold px-3"
                      >
                        {fetchingUrl ? "Fetching…" : "Fetch"}
                      </Button>
                    </div>
                    <Textarea
                      value={statsCsv}
                      onChange={(e) => setStatsCsv(e.target.value)}
                      placeholder={"[10 min ago] KILL: GhostFoxx(Allies/xxx) -> RommelJr(Axis/yyy) with STG44"}
                      data-testid="reps-pull-rcon-paste"
                      className="mt-2 rounded-none bg-[#05050A] border-[#00FFB2]/40 font-mono text-xs min-h-32"
                    />
                  </>
                )}
                {pullMode === "rcon" && (
                  <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#00FFB2]" data-testid="reps-pull-note-rcon">
                    · Live HLL RCON — requires HLL_RCON_HOST / PORT / PASSWORD env vars. Falls back to simulated when unset or unreachable.
                  </p>
                )}
                {pullMode === "csv" && (
                  <Textarea
                    value={statsCsv}
                    onChange={(e) => setStatsCsv(e.target.value)}
                    placeholder={"handle,kills,deaths,assists,mvp\nGhostFoxx,32,14,6,1\nRommelJr,21,18,3,"}
                    data-testid="reps-pull-csv"
                    className="mt-2 rounded-none bg-[#05050A] border-[#232330] font-mono text-xs min-h-24"
                  />
                )}
              </div>
              <Button
                onClick={submitScore}
                data-testid="reps-score-submit"
                className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
              >
                <Save className="w-4 h-4 mr-2" /> Submit result
              </Button>
              {/* Iter33 · Handle autocomplete for unmatched RCON handles */}
              {statsSuggestions && Object.keys(statsSuggestions).length > 0 && (
                <div className="mt-3 border border-[#F4D03F] bg-[#F4D03F]/5 p-3" data-testid="reps-suggestions">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-2">
                    Unmatched handles · did you mean?
                  </div>
                  <div className="space-y-2">
                    {Object.entries(statsSuggestions).map(([bad, picks]) => (
                      <div key={bad} className="flex items-center gap-2 flex-wrap" data-testid={`reps-suggestion-${bad}`}>
                        <span className="text-xs font-mono text-white/80">{bad} →</span>
                        {picks.map((p) => (
                          <button
                            key={p.player_id || p.handle}
                            type="button"
                            onClick={() => {
                              setStatsCsv((prev) => prev.replaceAll(bad, p.handle));
                              toast.success(`Replaced ${bad} → ${p.handle}. Submit again.`);
                            }}
                            data-testid={`reps-suggestion-pick-${p.handle}`}
                            className="text-[10px] font-mono uppercase tracking-widest border border-cyan-400/60 text-cyan-400 hover:bg-cyan-400 hover:text-black px-2 py-0.5"
                            title={`${Math.round(p.score * 100)}% match`}
                          >
                            {p.handle} · {Math.round(p.score * 100)}%
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter30 — Coin toss faction picker (moved from Schedule) */}
      <Dialog open={!!tossFor} onOpenChange={(o) => !o && setTossFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Coins className="w-4 h-4 text-[#F4D03F]" /> Pick your faction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              You won the toss — pick a side and the other rep is locked to the opposite.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setTossFaction("allies")}
                data-testid="reps-toss-pick-allies"
                className={`border p-6 text-center transition-colors ${
                  tossFaction === "allies" ? "border-cyan-400 bg-cyan-400/10 text-cyan-400" : "border-[#232330] text-gray-400 hover:text-white"
                }`}
              >
                <div className="font-heading font-black text-2xl uppercase tracking-tight">ALLIES</div>
                <div className="text-[10px] font-mono uppercase tracking-widest mt-1">US · UK · CCCP</div>
              </button>
              <button
                onClick={() => setTossFaction("axis")}
                data-testid="reps-toss-pick-axis"
                className={`border p-6 text-center transition-colors ${
                  tossFaction === "axis" ? "border-red-500 bg-red-500/10 text-red-400" : "border-[#232330] text-gray-400 hover:text-white"
                }`}
              >
                <div className="font-heading font-black text-2xl uppercase tracking-tight">AXIS</div>
                <div className="text-[10px] font-mono uppercase tracking-widest mt-1">Wehrmacht</div>
              </button>
            </div>
            <Button
              onClick={async () => {
                if (!tossFor) return;
                try {
                  await api.post(`/fixtures/${tossFor.id}/coin-toss/faction`, null, { params: { faction: tossFaction } });
                  toast.success(`Locked ${tossFaction.toUpperCase()}.`);
                  setTossFor(null);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              data-testid="reps-toss-lock-faction"
              className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
            >
              Lock {tossFaction.toUpperCase()}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter33: Match Planner chat dialog */}
      <Dialog open={!!chatProposal} onOpenChange={(o) => !o && setChatProposal(null)}>
        <DialogContent className="bg-[#101015] border-[#B084FF] text-white rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#B084FF]" />
              Planner chat · {chatProposal?.from_team_tag} vs {chatProposal?.to_team_tag}
            </DialogTitle>
          </DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
            {chatProposal ? new Date(chatProposal.scheduled_at).toLocaleString() : ""} · Map: {chatProposal?.map || "TBD"}
          </div>
          <div
            data-testid="planner-chat-thread"
            className="border border-[#232330] bg-[#05050A] p-3 h-64 overflow-y-auto space-y-2"
          >
            {chatLoading && <div className="text-center text-[10px] font-mono text-gray-500">Loading…</div>}
            {!chatLoading && chatMessages.length === 0 && (
              <div className="text-center text-[10px] font-mono uppercase tracking-widest text-gray-500">No messages yet — start the conversation.</div>
            )}
            {chatMessages.map((m) => {
              const mine = m.user_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`planner-chat-msg-${m.id}`}
                    className={`max-w-[80%] p-2 text-xs ${mine ? "bg-[#B084FF]/20 border border-[#B084FF]/60 text-white" : "bg-[#101015] border border-[#232330] text-gray-200"}`}
                  >
                    <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500 mb-0.5">
                      {m.author_name || (m.author_email || "").split("@")[0]} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="whitespace-pre-wrap break-words">{m.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendProposalChat(); } }}
              placeholder="Message the other rep..."
              data-testid="planner-chat-input"
              className="rounded-none bg-[#05050A] border-[#232330] text-sm"
            />
            <Button
              onClick={sendProposalChat}
              disabled={!chatDraft.trim()}
              data-testid="planner-chat-send"
              className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter32: Counter-proposal dialog */}
      <Dialog open={!!counterFor} onOpenChange={(o) => !o && setCounterFor(null)}>
        <DialogContent className="bg-[#101015] border-[#F4D03F] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-[#F4D03F]" />
              Counter-propose · {counterFor?.from_team_tag} vs {counterFor?.to_team_tag}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">New kickoff (UTC)</Label>
              <Input
                type="datetime-local"
                value={counter.scheduled_at}
                onChange={(e) => setCounter({ ...counter, scheduled_at: e.target.value })}
                data-testid="counter-when"
                className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Map (optional)</Label>
              <select
                value={counter.map}
                onChange={(e) => setCounter({ ...counter, map: e.target.value })}
                data-testid="counter-map"
                className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              >
                <option value="">— keep same —</option>
                {["Wheelspin","Stalingrad","Kursk","Kharkov","El Alamein","Foy","Hurtgen Forest","Utah Beach","Omaha Beach","Sainte-Mère-Église","Sainte-Marie-du-Mont","Carentan","Purple Heart Lane","Hill 400","Driel","Remagen","Mortain","Elsenborn Ridge"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Note (optional)</Label>
              <Input
                value={counter.note}
                onChange={(e) => setCounter({ ...counter, note: e.target.value })}
                data-testid="counter-note"
                className="rounded-none bg-[#05050A] border-[#232330] mt-1 text-sm"
              />
            </div>
            <Button
              onClick={submitCounter}
              data-testid="counter-submit"
              className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
            >
              Send counter-proposal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
